import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, del } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Skeleton } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { GripVertical, Plus, Pencil, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const EMPTY_PIPELINE = { name: "", description: "", color: "#3B82F6" };
const EMPTY_COLUMN = { name: "", wip_limit: "", is_done: false };

export default function PmPipelines() {
  const qc = useQueryClient();
  const { can } = useApp();
  const [pipeModal, setPipeModal] = useState(null);
  const [colModal, setColModal] = useState(null);
  const [dragCol, setDragCol] = useState(null);

  const { data: pipelines, isLoading } = useQuery({ queryKey: ["pm-pipelines"], queryFn: () => get("/pm/pipelines") });

  const createPipe = useMutation({
    mutationFn: (body) => post("/pm/pipelines", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pm-pipelines"] }); toast.success("Pipeline created"); setPipeModal(null); },
  });
  const updatePipe = useMutation({
    mutationFn: ({ id, body }) => patch(`/pm/pipelines/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pm-pipelines"] }); toast.success("Pipeline updated"); setPipeModal(null); },
  });
  const deletePipe = useMutation({
    mutationFn: (id) => del(`/pm/pipelines/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pm-pipelines"] }); toast.success("Pipeline deleted"); },
    onError: () => toast.error("Can't delete — projects still use this pipeline"),
  });
  const deleteCol = useMutation({
    mutationFn: ({ pid, cid }) => del(`/pm/pipelines/${pid}/columns/${cid}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pm-pipelines"] }); toast.success("Column deleted"); },
  });
  const saveColumns = useMutation({
    mutationFn: ({ pid, columns }) => patch(`/pm/pipelines/${pid}`, { columns }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pm-pipelines"] }),
  });

  const upsertColumn = (pipeline, body, editingId) => {
    const columns = editingId
      ? pipeline.columns.map((c) => (c.id === editingId ? { ...c, ...body } : c))
      : [...pipeline.columns, { ...body, id: `col-${crypto.randomUUID().slice(0, 8)}`, order: pipeline.columns.length }];
    saveColumns.mutate({ pid: pipeline.id, columns });
    setColModal(null);
  };

  const reorder = (pipeline, fromId, toId) => {
    if (fromId === toId) return;
    const columns = [...pipeline.columns];
    const fromIdx = columns.findIndex((c) => c.id === fromId);
    const toIdx = columns.findIndex((c) => c.id === toId);
    const [moved] = columns.splice(fromIdx, 1);
    columns.splice(toIdx, 0, moved);
    saveColumns.mutate({ pid: pipeline.id, columns: columns.map((c, i) => ({ ...c, order: i })) });
  };

  if (isLoading) return <Skeleton className="h-64" />;

  return (
    <div>
      <PageHeader title="Board Templates" subtitle="Pipelines define the columns a new project's board starts with.">
        {can("pm", "edit") && (
          <Button data-testid="new-pm-pipeline-btn" onClick={() => setPipeModal({ mode: "new", data: EMPTY_PIPELINE })}>
            <Plus className="mr-1.5 h-4 w-4" />New Pipeline
          </Button>
        )}
      </PageHeader>

      {pipelines?.map((p) => (
        <Section key={p.id} title={
          <span className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full" style={{ backgroundColor: p.color }} />
            {p.name}
            <span className="text-xs font-normal text-muted-foreground">{p.project_count} project{p.project_count === 1 ? "" : "s"}</span>
          </span>
        } className="mb-4" testid={`pm-pipeline-${p.id}`}
          action={
            <div className="flex items-center gap-2">
              {p.is_default && <StatusBadge family="blue" label="Default" />}
              {can("pm", "edit") && (
                <>
                  <Button variant="ghost" size="icon" data-testid={`edit-pm-pipeline-${p.id}`}
                    onClick={() => setPipeModal({ mode: "edit", data: p })}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" data-testid={`add-pm-column-${p.id}`}
                    onClick={() => setColModal({ mode: "new", pipeline: p, data: EMPTY_COLUMN })}>
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
              {can("pm", "delete") && (
                <ConfirmDelete testid={`del-pm-pipeline-${p.id}`} title={`Delete "${p.name}"?`}
                  description="Blocked if any project still uses this pipeline."
                  trigger={<Button variant="ghost" size="icon"><Trash2 className="h-3.5 w-3.5" /></Button>}
                  onConfirm={() => deletePipe.mutate(p.id)} />
              )}
            </div>
          }>
          <div className="divide-y divide-border">
            {[...p.columns].sort((a, b) => a.order - b.order).map((c) => (
              <div key={c.id} data-testid={`pm-column-row-${c.id}`}
                draggable={can("pm", "edit")}
                onDragStart={() => setDragCol({ pipelineId: p.id, colId: c.id })}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dragCol?.pipelineId === p.id) reorder(p, dragCol.colId, c.id); setDragCol(null); }}
                className={cn("flex items-center gap-3 px-4 py-3", dragCol?.colId === c.id && "opacity-50")}>
                <GripVertical className={cn("h-4 w-4 text-muted-foreground", can("pm", "edit") && "cursor-grab")} />
                <span className="flex-1 text-sm font-medium">{c.name}</span>
                {c.is_done && <StatusBadge family="green" label="Done column" />}
                <span className="text-xs text-muted-foreground">{c.wip_limit ? `WIP ${c.wip_limit}` : "No WIP limit"}</span>
                {can("pm", "edit") && (
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" data-testid={`edit-pm-column-${c.id}`}
                      onClick={() => setColModal({ mode: "edit", pipeline: p, data: c })}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <ConfirmDelete testid={`del-pm-column-${c.id}`} title={`Delete "${c.name}" column?`}
                      trigger={<Button variant="ghost" size="icon"><Trash2 className="h-3.5 w-3.5" /></Button>}
                      onConfirm={() => deleteCol.mutate({ pid: p.id, cid: c.id })} />
                  </div>
                )}
              </div>
            ))}
            {p.columns.length === 0 && <div className="px-4 py-6 text-center text-xs text-muted-foreground">No columns yet.</div>}
          </div>
        </Section>
      ))}

      <PipelineModal state={pipeModal} setState={setPipeModal}
        onSubmit={(body) => pipeModal?.mode === "new" ? createPipe.mutate(body) : updatePipe.mutate({ id: pipeModal.data.id, body })} />
      <ColumnModal state={colModal} setState={setColModal}
        onSubmit={(body) => upsertColumn(colModal.pipeline, body, colModal.mode === "edit" ? colModal.data.id : null)} />
    </div>
  );
}

function PipelineModal({ state, setState, onSubmit }) {
  const [form, setForm] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const d = state?.data || EMPTY_PIPELINE;
  const val = (k) => (form[k] !== undefined ? form[k] : d[k]);

  return (
    <Dialog open={!!state} onOpenChange={(v) => { if (!v) setState(null); else setForm({}); }} key={d.id || "new-pmpipe"}>
      <DialogContent className="max-w-md" data-testid="pm-pipeline-modal">
        <DialogHeader><DialogTitle>{state?.mode === "edit" ? "Edit Pipeline" : "New Pipeline"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input data-testid="pmpipe-name" className="mt-1" value={val("name") || ""} onChange={(e) => set("name", e.target.value)} /></div>
          <div><Label>Description</Label><Textarea className="mt-1" value={val("description") || ""} onChange={(e) => set("description", e.target.value)} /></div>
          <div>
            <Label>Color</Label>
            <div className="mt-1 flex items-center gap-2">
              <input type="color" data-testid="pmpipe-color" className="h-9 w-12 cursor-pointer rounded border border-border bg-transparent"
                value={val("color") || "#3B82F6"} onChange={(e) => set("color", e.target.value)} />
              <Input value={val("color") || "#3B82F6"} onChange={(e) => set("color", e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setState(null)}>Cancel</Button>
          <Button data-testid="pmpipe-submit" onClick={() => {
            if (!val("name")) { toast.error("Name is required"); return; }
            onSubmit({ name: val("name"), description: val("description") || "", color: val("color") || "#3B82F6" });
          }}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ColumnModal({ state, setState, onSubmit }) {
  const [form, setForm] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const d = state?.data || EMPTY_COLUMN;
  const val = (k) => (form[k] !== undefined ? form[k] : d[k]);

  return (
    <Dialog open={!!state} onOpenChange={(v) => { if (!v) setState(null); else setForm({}); }} key={d.id || "new-pmcol"}>
      <DialogContent className="max-w-sm" data-testid="pm-column-modal">
        <DialogHeader><DialogTitle>{state?.mode === "edit" ? "Edit Column" : "Add Column"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Column Name *</Label><Input data-testid="pmcol-name" className="mt-1" value={val("name") || ""} onChange={(e) => set("name", e.target.value)} /></div>
          <div><Label>WIP Limit</Label><Input type="number" data-testid="pmcol-wip" className="mt-1" value={val("wip_limit") ?? ""} onChange={(e) => set("wip_limit", e.target.value ? Number(e.target.value) : null)} /></div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="pmcol-is-done" checked={!!val("is_done")} onChange={(e) => set("is_done", e.target.checked)} />
            This is the "Done" column
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setState(null)}>Cancel</Button>
          <Button data-testid="pmcol-submit" onClick={() => {
            if (!val("name")) { toast.error("Column name is required"); return; }
            onSubmit({ name: val("name"), wip_limit: val("wip_limit") ?? null, is_done: !!val("is_done") });
          }}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
